/**
 * Playback telemetry for the course (percorso) videos.
 *
 * Members report that Free journey videos freeze, and until now the only
 * numbers were the members who wrote in chat. Each course player records what
 * actually happened to a playback — when it was asked to start, when the first
 * frame appeared, every freeze, error and fallback, whether the member left
 * right after a problem (see CustomVimeoPlayer/useVideoTelemetry.js) — and this
 * module keeps it and delivers it to POST /api/v1/video-playback/track, which
 * feeds the CRM page "Video Percorso".
 *
 * Separate from funnelTracker.js on purpose: its own endpoint, queue and rate
 * limit, so a problem in one can never cost the other its data.
 *
 * THE ONE RULE, as for the funnel: telemetry must never be able to break
 * playback or the app. Every export swallows its own failures and nothing here
 * awaits the network on a member's action.
 */
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import config from "../settings/config";
import { getAuthToken, isPerTabSession } from "./authStorage";
import { ANONYMOUS_ID_KEY } from "./funnelTracker";

/** The server contract (TrackVideoPlaybackRequest on the backend). */
export const TELEMETRY_VERSION = "1";

/**
 * Bumped whenever playback BEHAVIOUR changes — a player fix, an hls.js upgrade —
 * so the dashboard can compare before and after on the same definitions.
 *
 * p1: first release with telemetry (HLS capped at 720p, pill completion retried).
 */
export const PLAYER_VERSION = "p1";

const ENDPOINT = "/api/v1/video-playback/track";
const QUEUE_KEY = "ds_video_telemetry_queue";
const OPEN_KEY = "ds_video_telemetry_open";
const PAUSED_KEY = "ds_video_telemetry_paused_until";

const MAX_QUEUE = 300;
const MAX_OPEN_SESSIONS = 20;
const FLUSH_DEBOUNCE_MS = 5000;
// Browsers refuse a keepalive request whose body is over 64 KB.
const MAX_BODY_CHARS = 48 * 1024;
const MAX_SESSIONS = 50; // the endpoint's caps
const MAX_ISSUES = 100;
const RETRY_BASE_MS = 30 * 1000;
const RETRY_MAX_MS = 10 * 60 * 1000;
// An "open" session nobody has touched for this long belongs to an app that
// was closed mid-video. Shorter would risk ending one another tab is playing.
const STALE_OPEN_MS = 2 * 60 * 1000;
const SERVER_PAUSE_MS = 24 * 60 * 60 * 1000;

let userId = null;
let flushTimer = null;
let retryTimer = null;
let retryDelay = RETRY_BASE_MS;
let inflight = null;
let started = false;
let appVersion = null;
let appVersionResolved = false;
let bundleVersion = null;
let bundleVersionResolved = false;
const pillTrackers = new Map();

function safeGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (_) {
    return null;
  }
}

function safeSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (_) {
    /* storage unavailable — telemetry degrades, the app does not */
  }
}

function readJson(key, fallback) {
  try {
    const raw = safeGet(key);
    const parsed = raw ? JSON.parse(raw) : fallback;
    return parsed ?? fallback;
  } catch (_) {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    safeSet(key, JSON.stringify(value));
  } catch (_) {
    /* ignore */
  }
}

/** A v4 UUID: the backend validates the shape of every id. */
export function newTelemetryId() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (_) {
    /* fall through */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function baseUrl() {
  return config?.env?.[config?.env?.mode]?.url || "";
}

function platform() {
  try {
    const p = Capacitor.getPlatform();
    return p === "ios" || p === "android" ? p : "web";
  } catch (_) {
    return "web";
  }
}

/**
 * The signed-in member, set from the app root. Every record carries it, and a
 * record is only ever sent with that same member's token — a queue left on the
 * phone by a previous account is discarded rather than attributed to the next.
 */
export function setVideoTelemetryUser(id) {
  const next = Number(id) > 0 ? Number(id) : null;
  if (next === userId) return;
  userId = next;
  if (next) scheduleFlush();
}

export function getVideoTelemetryUser() {
  return userId;
}

function pausedByServer() {
  return Number(safeGet(PAUSED_KEY) || 0) > Date.now();
}

/** Should a player record anything right now? */
export function isVideoTelemetryActive() {
  try {
    // A support operator inside a member's account (opened from the CRM) must
    // not add their own viewing to that member's numbers.
    return !!userId && !isPerTabSession() && !pausedByServer();
  } catch (_) {
    return false;
  }
}

function readQueue() {
  const queue = readJson(QUEUE_KEY, []);
  return Array.isArray(queue) ? queue : [];
}

function writeQueue(queue) {
  writeJson(QUEUE_KEY, queue.length > MAX_QUEUE ? queue.slice(queue.length - MAX_QUEUE) : queue);
}

/**
 * Coalesced by record: the queue holds only the newest snapshot of each
 * session (and of each problem), so a long session is one item, not dozens.
 */
function enqueue(kind, record) {
  if (!record || !record.id || !record.uid) return;

  const key = `${kind}:${record.id}`;
  const seq = Number(record.seq) || 0;
  const queue = readQueue();
  const index = queue.findIndex((item) => item.key === key);

  if (index >= 0) {
    if ((Number(queue[index].seq) || 0) >= seq) return;
    queue.splice(index, 1);
  }

  queue.push({ key, kind, uid: record.uid, seq, data: record });
  writeQueue(queue);
}

export function persistOpenSession(snapshot) {
  try {
    if (!snapshot?.id) return;

    const open = readJson(OPEN_KEY, {});
    open[snapshot.id] = { touched_at: Date.now(), snapshot };

    const ids = Object.keys(open);
    if (ids.length > MAX_OPEN_SESSIONS) {
      ids
        .sort((a, b) => (open[a].touched_at || 0) - (open[b].touched_at || 0))
        .slice(0, ids.length - MAX_OPEN_SESSIONS)
        .forEach((id) => delete open[id]);
    }

    writeJson(OPEN_KEY, open);
  } catch (_) {
    /* ignore */
  }
}

function forgetOpenSession(id) {
  try {
    const open = readJson(OPEN_KEY, {});
    if (open[id]) {
      delete open[id];
      writeJson(OPEN_KEY, open);
    }
  } catch (_) {
    /* ignore */
  }
}

/** Queue a session snapshot. An ended session leaves the open-session list. */
export function recordSessionSnapshot(snapshot, { flush = false } = {}) {
  try {
    if (!snapshot || !isVideoTelemetryActive()) return;

    enqueue("session", snapshot);

    if (snapshot.ended_at) forgetOpenSession(snapshot.id);
    else persistOpenSession(snapshot);

    if (flush) flushVideoTelemetry();
    else scheduleFlush();
  } catch (_) {
    /* telemetry never throws into the app */
  }
}

export function recordIssue(issue) {
  try {
    if (!issue || !isVideoTelemetryActive()) return;
    enqueue("issue", issue);
    scheduleFlush();
  } catch (_) {
    /* telemetry never throws into the app */
  }
}

/**
 * Sessions a closed app never finished: ended where they were last seen, as
 * `app_closed`. The server would eventually treat them as ended anyway; doing
 * it here keeps the reason and the moment exact.
 */
function closeAbandonedSessions(now = Date.now()) {
  const open = readJson(OPEN_KEY, {});
  let changed = false;

  Object.keys(open).forEach((id) => {
    const entry = open[id];
    const snapshot = entry?.snapshot;

    if (!snapshot) {
      delete open[id];
      changed = true;
      return;
    }

    if (now - (Number(entry.touched_at) || 0) < STALE_OPEN_MS) return;

    enqueue("session", {
      ...snapshot,
      seq: (Number(snapshot.seq) || 0) + 1,
      ended_at: snapshot.last_activity_at || snapshot.intent_at,
      end_reason: "app_closed",
    });

    delete open[id];
    changed = true;
  });

  if (changed) writeJson(OPEN_KEY, open);
}

async function resolveAppVersion() {
  if (appVersionResolved) return appVersion;
  appVersionResolved = true;
  try {
    if (Capacitor.isNativePlatform()) {
      const info = await CapacitorApp.getInfo();
      appVersion = info?.version ?? null;
    }
  } catch (_) {
    appVersion = null;
  }
  return appVersion;
}

/**
 * The OTA (Capgo) bundle actually running. The store version alone cannot say
 * whether a phone has received a JavaScript fix yet.
 */
async function resolveBundleVersion() {
  if (bundleVersionResolved) return bundleVersion;
  bundleVersionResolved = true;
  try {
    if (Capacitor.isNativePlatform()) {
      const current = await CapacitorUpdater.current();
      bundleVersion = current?.bundle?.version ?? null;
    }
  } catch (_) {
    bundleVersion = null;
  }
  return bundleVersion;
}

function numberOrNull(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function clientContext() {
  const nav = typeof navigator !== "undefined" ? navigator : {};
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection || null;

  return {
    user_agent: String(nav.userAgent || "").slice(0, 1024),
    device_memory: numberOrNull(nav.deviceMemory),
    cpu_cores: numberOrNull(nav.hardwareConcurrency),
    screen_w: numberOrNull(window.screen?.width),
    screen_h: numberOrNull(window.screen?.height),
    dpr: numberOrNull(window.devicePixelRatio),
    network_type: connection?.effectiveType || null,
    downlink: numberOrNull(connection?.downlink),
    save_data: typeof connection?.saveData === "boolean" ? connection.saveData : null,
  };
}

function scheduleFlush(delay = FLUSH_DEBOUNCE_MS) {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushVideoTelemetry();
  }, delay);
}

function scheduleRetry() {
  if (retryTimer) return;
  const delay = retryDelay;
  retryDelay = Math.min(RETRY_MAX_MS, retryDelay * 2);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    flushVideoTelemetry();
  }, delay);
}

/** Send what is queued. At-least-once: nothing leaves storage without a 2xx. */
export function flushVideoTelemetry() {
  if (inflight) return inflight;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  inflight = send().finally(() => {
    inflight = null;
  });
  return inflight;
}

async function send() {
  try {
    const uid = userId;
    const token = getAuthToken();
    if (!uid || !token || isPerTabSession()) return;

    let queue = readQueue();
    if (!queue.length) return;

    if (pausedByServer()) {
      writeQueue([]);
      return;
    }

    if (queue.some((item) => item.uid !== uid)) {
      queue = queue.filter((item) => item.uid === uid);
      writeQueue(queue);
      if (!queue.length) return;
    }

    const [version, bundle] = await Promise.all([resolveAppVersion(), resolveBundleVersion()]);

    const envelope = {
      telemetry_version: TELEMETRY_VERSION,
      player_version: PLAYER_VERSION,
      platform: platform(),
      app_version: version,
      bundle_version: bundle,
      device_id: safeGet(ANONYMOUS_ID_KEY) || null,
      client: clientContext(),
    };

    const sessions = [];
    const issues = [];
    const sent = [];
    let size = JSON.stringify(envelope).length + 200;

    for (const item of queue) {
      if (item.kind !== "session" && item.kind !== "issue") continue;
      if (item.kind === "session" && sessions.length >= MAX_SESSIONS) continue;
      if (item.kind === "issue" && issues.length >= MAX_ISSUES) continue;

      const itemSize = JSON.stringify(item.data).length + 1;
      if (sent.length && size + itemSize > MAX_BODY_CHARS) break;

      (item.kind === "session" ? sessions : issues).push(item.data);
      sent.push(item);
      size += itemSize;
    }

    if (!sent.length) return;

    let response;
    try {
      response = await fetch(`${baseUrl()}${ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        // Lets the last batch of a session outlive the page. Only when it fits:
        // an oversized keepalive request is refused outright.
        keepalive: size < 60 * 1024,
        body: JSON.stringify({ ...envelope, sent_at: Date.now(), sessions, issues }),
      });
    } catch (_) {
      scheduleRetry();
      return;
    }

    // A 2xx is delivered. 400/413/422 will never be accepted, and retrying
    // them would block everything queued behind. Anything else — 404 from an
    // older backend, 429, 5xx — stays queued for later.
    const rejected = response.status === 400 || response.status === 413 || response.status === 422;
    if (!response.ok && !rejected) {
      scheduleRetry();
      return;
    }

    retryDelay = RETRY_BASE_MS;

    if (response.ok) {
      let body = null;
      try {
        body = await response.json();
      } catch (_) {
        body = null;
      }

      // The server's kill switch: stop recording for a day.
      if (body && body.enabled === false) {
        safeSet(PAUSED_KEY, String(Date.now() + SERVER_PAUSE_MS));
        writeQueue([]);
        writeJson(OPEN_KEY, {});
        return;
      }
    }

    // Re-read: anything recorded while the request was in flight is in storage
    // now, and a newer snapshot of a record just sent must survive.
    const sentSeq = new Map(sent.map((item) => [item.key, item.seq]));
    const remaining = readQueue().filter(
      (item) => !sentSeq.has(item.key) || item.seq > sentSeq.get(item.key),
    );
    writeQueue(remaining);

    if (remaining.some((item) => item.uid === uid)) scheduleFlush(1000);
  } catch (_) {
    /* telemetry never throws into the app */
  }
}

/**
 * The pill page reports whether saving a pill's completion worked; the player
 * that played that pill attaches it to its session.
 *
 * @returns {() => void} unregister
 */
export function registerPillTracker(postId, handler) {
  if (postId == null || typeof handler !== "function") return () => {};

  const key = String(postId);
  pillTrackers.set(key, handler);

  return () => {
    if (pillTrackers.get(key) === handler) pillTrackers.delete(key);
  };
}

/** @param {{result: "ok"|"failed", httpStatus?: number|null}} result */
export function recordPillCompletion(postId, result) {
  try {
    const handler = pillTrackers.get(String(postId));
    if (handler) handler(result || {});
  } catch (_) {
    /* ignore */
  }
}

/** Once, at app start: finish what a closed app left open and start flushing. */
export function startVideoTelemetry() {
  if (started) return;
  started = true;

  try {
    closeAbandonedSessions();
  } catch (_) {
    /* ignore */
  }

  try {
    // Plain DOM events on purpose. The Capacitor App plugin's listeners can be
    // removed wholesale by other code; these cannot. Capacitor also dispatches
    // `pause` on document when the native app goes to the background.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") flushVideoTelemetry();
    });
    document.addEventListener("pause", () => flushVideoTelemetry());
    window.addEventListener("pagehide", () => flushVideoTelemetry());
  } catch (_) {
    /* ignore */
  }

  scheduleFlush(3000);
}

/** Test-only access to module state. */
export const __TEST_ONLY__ = {
  QUEUE_KEY,
  OPEN_KEY,
  PAUSED_KEY,
  readQueue,
  reset() {
    if (flushTimer) clearTimeout(flushTimer);
    if (retryTimer) clearTimeout(retryTimer);
    flushTimer = null;
    retryTimer = null;
    retryDelay = RETRY_BASE_MS;
    inflight = null;
    started = false;
    userId = null;
    appVersion = null;
    appVersionResolved = false;
    bundleVersion = null;
    bundleVersionResolved = false;
    pillTrackers.clear();
  },
};
